import { PDFDocument } from 'pdf-lib';

// Parsing very large PDFs in the browser is expensive, so above this size we
// only check that the file is readable.
const MAX_PDF_INSPECT_BYTES = 50 * 1024 * 1024;

const isPdf = (file) => file.type === 'application/pdf' || /\.pdf$/i.test(file.name || '');

const readAsArrayBuffer = (blob) => {
    if (typeof blob.arrayBuffer === 'function') {
        return blob.arrayBuffer();
    }

    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result);
        reader.onerror = () => reject(reader.error || new Error('Could not read file'));
        reader.readAsArrayBuffer(blob);
    });
};

// Returns a reason why the file cannot be uploaded, or null when it looks fine.
const getSkipReason = async (file) => {
    if (!file || typeof file.size !== 'number') {
        return 'File could not be read.';
    }

    if (file.size === 0) {
        return 'File is empty (0 bytes).';
    }

    if (isPdf(file) && file.size <= MAX_PDF_INSPECT_BYTES) {
        const buffer = await readAsArrayBuffer(file);

        if (!buffer || buffer.byteLength === 0) {
            return 'File is empty (0 bytes).';
        }

        const pdf = await PDFDocument.load(buffer, { ignoreEncryption: true });

        if (pdf.getPageCount() === 0) {
            return 'PDF does not contain any pages.';
        }

        return null;
    }

    await readAsArrayBuffer(file.slice(0, 1));

    return null;
};

export default async function validateFiles(files) {
    const validFiles = [];
    const skippedFiles = [];

    for (const file of files) {
        try {
            const reason = await getSkipReason(file);

            if (reason) {
                skippedFiles.push({ name: file?.name || 'Unknown file', reason });
            } else {
                validFiles.push(file);
            }
        } catch (error) {
            skippedFiles.push({
                name: file?.name || 'Unknown file',
                reason: 'File is damaged or could not be read.',
            });
        }
    }

    return { validFiles, skippedFiles };
}
